/**
 * AI Census Actions Service
 * Provides AI-compatible actions for CensusAverageBox component
 */

import { setPercentageBy, setCustomePercentages } from '../store/reducers/common.slice';
import { percentageByEnum } from '../data/common.data';
import { getPercentageByTotal } from '../utilis/common';

class AICensusActionsService {
    constructor() {
        this.dispatch = null;
        this.getState = null;
        this.censusHandler = null; // Handler from CensusAverageBox component
        this.pageType = null; // 'hospital', 'communityTransfer', or 'deceased'
    }

    /**
     * Initialize the service with dispatch and getState functions
     * @param {Function} dispatch - Redux dispatch function
     * @param {Function} getState - Redux getState function
     */
    initialize(dispatch, getState) {
        this.dispatch = dispatch;
        this.getState = getState;
        
        // Detect page type from state structure
        const state = getState();
        if (state.hospital) {
            this.pageType = 'hospital';
        } else if (state.communityTransfer) {
            this.pageType = 'communityTransfer';
        } else if (state.deceased) {
            this.pageType = 'deceased';
        }
    }

    /**
     * Register the CensusAverageBox handler (same pattern as date actions)
     * @param {Function} handler - The handleChangePercentage function from CensusAverageBox
     */
    registerCensusHandler(handler) {
        this.censusHandler = handler;
        // console.log('📊 Census handler registered:', !!handler);
    }

    /**
     * Get the registered census handler
     * @returns {Function|null} The registered handler
     */
    getCensusHandler() {
        return this.censusHandler;
    }

    /**
     * Get current percentage calculation method
     * @returns {Object} Current percentage settings
     */
    getCurrentPercentageSettings() {
        if (!this.getState) {
            throw new Error('Service not initialized. Call initialize() first.');
        }
        
        const state = this.getState();
        const { percentageBy, customPercentageLabel, customePercentages } = state.common;
        const pageState = state[this.pageType] || state.hospital || state.communityTransfer || state.deceased || {};
        const { dbData } = pageState;
        
        return {
            currentMethod: percentageBy,
            customLabel: customPercentageLabel,
            customPercentages: customePercentages,
            availableMethods: this.getAvailablePercentageMethods(dbData),
            censusData: this.getCensusData(dbData)
        };
    }

    /**
     * Get available percentage calculation methods
     * @param {Object} dbData - Database data
     * @returns {Array} Available methods
     */
    getAvailablePercentageMethods(dbData) {
        const methods = [
            {
                key: 'bedCapacity',  // Use enum key, not display value
                label: 'Bed Capacity',
                value: dbData?.bedCapacity || 0,
                description: 'Calculate percentages based on total bed capacity',
                available: true
            }
        ];

        // Add census methods if not hospital-only dashboard
        if (this.getState) {
            const { isOnlyHospitalDashboard = false } = this.getState().dashboardData || {};
            if (!isOnlyHospitalDashboard) {
                methods.push(
                    {
                        key: 'censusAsOfNow',  // Use enum key, not display value
                        label: `Census as of ${this.formatDate(dbData?.censusEndDate)}`,
                        value: dbData?.censusAsOfNow || 0,
                        description: 'Calculate percentages based on current census',
                        available: true
                    },
                    {
                        key: 'censusAverage',  // Use enum key, not display value
                        label: 'Average Occupancy',
                        value: getPercentageByTotal(dbData) || 0,
                        description: 'Calculate percentages based on average census of selected time frame',
                        available: true
                    }
                );
            }
        }

        return methods;
    }

    /**
     * Get census data for display
     * @param {Object} dbData - Database data
     * @returns {Object} Census data
     */
    getCensusData(dbData) {
        return {
            bedCapacity: dbData?.bedCapacity || 0,
            censusAsOfNow: dbData?.censusAsOfNow || 0,
            censusEndDate: dbData?.censusEndDate || null,
            censusAverage: getPercentageByTotal(dbData) || 0
        };
    }

    /**
     * Change percentage calculation method
     * @param {string} method - Method to switch to
     * @returns {Object} Result of the action
     */
    changePercentageMethod(method) {
        try {
            if (!this.dispatch || !this.getState) {
                throw new Error('Service not initialized. Call initialize() first.');
            }

            const state = this.getState();
            const pageState = state[this.pageType] || state.hospital || state.communityTransfer || {};
            const availableMethods = this.getAvailablePercentageMethods(pageState.dbData);
            const methodExists = availableMethods.some(m => m.key === method);
            
            if (!methodExists) {
                console.error('🚨 AI Census Debug - Method not found:', {
                    requestedMethod: method,
                    requestedMethodType: typeof method,
                    availableMethodKeys: availableMethods.map(m => m.key),
                    availableMethodsDetails: availableMethods.map(m => ({ key: m.key, keyType: typeof m.key, label: m.label }))
                });
                return {
                    success: false,
                    error: `Percentage method '${method}' is not available. Available methods: ${availableMethods.map(m => m.key).join(', ')}`,
                    availableMethods: availableMethods.map(m => m.key)
                };
            }

            // Dispatch the action to actually change the percentage method
            this.dispatch(setPercentageBy(method));

            // Get the method details for confirmation
            const methodDetails = availableMethods.find(m => m.key === method);
            const methodLabel = methodDetails ? methodDetails.label : method;

            return {
                success: true,
                message: `✅ Successfully switched to ${methodLabel} for percentage calculations`,
                newMethod: method,
                methodLabel: methodLabel,
                updatedSettings: this.getCurrentPercentageSettings(),
                actionPerformed: true
            };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Add custom percentage
     * @param {Object} customData - Custom percentage data
     * @returns {Object} Result of the action
     */
    addCustomPercentage(customData) {
        try {
            if (!this.dispatch || !this.getState) {
                throw new Error('Service not initialized. Call initialize() first.');
            }

            const { label, value } = customData;
            
            if (!label || !value) {
                return {
                    success: false,
                    error: 'Both label and value are required for custom percentage'
                };
            }

            const state = this.getState();
            const currentCustoms = state.common.customePercentages || [];
            const newCustom = {
                index: currentCustoms.length,
                customPercentage: parseFloat(value),
                label: label
            };

            this.dispatch(setCustomePercentages([...currentCustoms, newCustom]));

            return {
                success: true,
                message: `Added custom percentage: ${label} = ${value}`,
                updatedSettings: this.getCurrentPercentageSettings()
            };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Remove custom percentage
     * @param {number} index - Index of custom percentage to remove
     * @returns {Object} Result of the action
     */
    removeCustomPercentage(index) {
        try {
            if (!this.dispatch || !this.getState) {
                throw new Error('Service not initialized. Call initialize() first.');
            }

            const state = this.getState();
            const currentCustoms = state.common.customePercentages || [];
            
            if (index < 0 || index >= currentCustoms.length) {
                return {
                    success: false,
                    error: 'Invalid custom percentage index'
                };
            }

            const updatedCustoms = currentCustoms.filter((_, i) => i !== index);
            this.dispatch(setCustomePercentages(updatedCustoms));

            return {
                success: true,
                message: `Removed custom percentage at index ${index}`,
                updatedSettings: this.getCurrentPercentageSettings()
            };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get percentage calculation explanation
     * @param {string} method - Method to explain
     * @returns {Object} Explanation
     */
    getPercentageExplanation(method) {
        const explanations = {
            [percentageByEnum.bedCapacity]: {
                title: 'Bed Capacity',
                description: 'Percentages are calculated based on the total bed capacity of the facility. This shows how many beds are occupied relative to the total available beds.',
                calculation: 'Percentage = (Number of Transfers / Bed Capacity) × 100',
                useCase: 'Use this to understand bed utilization and capacity management.'
            },
            [percentageByEnum.censusAsOfNow]: {
                title: 'Current Census',
                description: 'Percentages are calculated based on the current number of residents in the facility at a specific point in time.',
                calculation: 'Percentage = (Number of Transfers / Current Census) × 100',
                useCase: 'Use this to understand current occupancy patterns and immediate capacity.'
            },
            [percentageByEnum.censusAverage]: {
                title: 'Average Occupancy',
                description: 'Percentages are calculated based on the average number of residents during the selected time frame.',
                calculation: 'Percentage = (Number of Transfers / Average Census) × 100',
                useCase: 'Use this to understand trends over time and compare against historical averages.'
            }
        };

        return explanations[method] || {
            title: 'Unknown Method',
            description: 'This percentage calculation method is not recognized.',
            calculation: 'N/A',
            useCase: 'N/A'
        };
    }

    /**
     * Format date for display
     * @param {Date|string} date - Date to format
     * @returns {string} Formatted date
     */
    formatDate(date) {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString();
    }

    /**
     * Get AI action suggestions based on current state
     * @returns {Array} Action suggestions
     */
    getAIActionSuggestions() {
        const settings = this.getCurrentPercentageSettings();
        const suggestions = [];

        // Suggest switching methods
        settings.availableMethods.forEach(method => {
            if (method.key !== settings.currentMethod) {
                suggestions.push({
                    action: `switch_to_${method.key}`,
                    description: `Switch to ${method.label} for percentage calculations`,
                    command: `Change percentage calculation to ${method.label}`,
                    method: method.key
                });
            }
        });

        // Suggest adding custom percentage
        suggestions.push({
            action: 'add_custom_percentage',
            description: 'Add a custom percentage calculation method',
            command: 'Add custom percentage calculation',
            method: 'custom'
        });

        return suggestions;
    }
}

// Create singleton instance
const aiCensusActionsService = new AICensusActionsService();

export default aiCensusActionsService;
